import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Upload, Database, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ThemeNotification } from '../components/ThemeNotification';

/**
 * Landing page — route: / (public, unauthenticated)
 *
 * Marketing page for the M16S platform. Entirely static — no API calls, no
 * state, no side effects. All interactivity is Framer Motion animations.
 *
 * Sections:
 *   1. Hero          — headline, CTA buttons (Get Started / Login), key stats
 *   2. How It Works  — three-step explainer cards (Upload → Analyze → Visualize)
 *   3. CTA           — secondary call-to-action with Request Access / Login
 *
 * The ThemeNotification component is rendered at the top to display a toast
 * when the user's OS-level colour scheme is detected.
 */
export default function Landing() {
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowWelcome(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ThemeNotification />

      {/* Hero Section */}
      <section className="relative px-4 pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        {/* Animated background elements - FULL WIDTH */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-40 left-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-300/30 to-pink-300/30 dark:from-purple-500/5 dark:to-pink-500/5 rounded-full blur-3xl"
        ></motion.div>

        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-br from-blue-300/20 to-purple-300/20 dark:from-blue-500/5 dark:to-purple-500/5 rounded-full blur-3xl"
        ></motion.div>

        {/* Floating particles — 6 small dots scattered across the hero, each
            animating independently with a staggered delay for a lively feel. */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-primary/30 rounded-full"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 3) * 20}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.5, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3,
            }}
          />
        ))}

        <div className="relative container mx-auto max-w-5xl text-center space-y-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Heading — "Welcome" overlays the real title while showWelcome is true.
                The title is always in the DOM so it sets the container height.
                No AnimatePresence needed — avoids the exit-frame flash entirely. */}
            <div className="relative space-y-2">
              {/* Real title — always rendered, drives container height */}
              <motion.h1
                animate={{ opacity: showWelcome ? 0 : 1, y: showWelcome ? 10 : 0 }}
                initial={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="text-6xl md:text-8xl font-bold tracking-tight leading-[1.1]"
              >
                Microbiome Analysis,{' '}
                <motion.span
                  animate={{ opacity: showWelcome ? 0 : 1 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
                  className="hero-gradient inline-block"
                >
                  Automated
                </motion.span>
              </motion.h1>

              {/* "Welcome" — absolutely overlaid, fades out when showWelcome flips */}
              <motion.h1
                animate={{ opacity: showWelcome ? 1 : 0, y: showWelcome ? 0 : -10 }}
                initial={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.45, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center text-6xl md:text-8xl font-bold tracking-tight leading-[1.1] pointer-events-none"
              >
                Welcome
              </motion.h1>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.6
              }}
              className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto font-light leading-relaxed"
            >
              Professional 16S rRNA sequencing analysis with automated pipelines,
              statistical testing, and publication-ready visualizations.
            </motion.p>
          </motion.div>

          {/* Buttons with enhanced hover effects */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.8,
              type: "spring",
              stiffness: 100
            }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" asChild className="px-8 h-14 text-base relative overflow-hidden group">
                <Link to="/signup" className="flex items-center gap-2">
                  {/* Animated shimmer gradient scrolls across the button on a loop. */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary"
                    animate={{
                      x: ['-100%', '100%'],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    style={{ width: '200%' }}
                  />
                  <span className="relative z-10">Sign Up</span>
                  <motion.div
                    className="relative z-10"
                    animate={{ x: [0, 5, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <ArrowRight className="h-5 w-5" />
                  </motion.div>
                </Link>
              </Button>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button size="lg" variant="outline" asChild className="px-8 h-14 text-base">
                <Link to="/login">Login</Link>
              </Button>
            </motion.div>
          </motion.div>

        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4">
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
      </div>

      {/* How it Works Section */}
      <section className="container mx-auto px-4 py-24 md:py-32">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Three Steps to Results
            </h2>
            <p className="text-xl text-muted-foreground font-light">
              Upload, analyze, and visualize—all automated
            </p>
          </motion.div>

          {/* Step cards — defined as a data array so all three share the same
              card template. Each animates in from below with a staggered delay
              based on its index. `viewport={{ once: true }}` prevents the
              animation from replaying on scroll. */}
          <div className="grid md:grid-cols-3 gap-12 relative">
            {[
              {
                icon: Upload,
                number: '01',
                title: 'Upload',
                description: 'Upload FASTQ files organized by barcode.',
                iconBg: 'bg-purple-200 dark:bg-purple-800/30',
                iconColor: 'text-purple-700 dark:text-purple-300',
                accentColor: 'from-purple-500 to-purple-600',
              },
              {
                icon: Database,
                number: '02',
                title: 'Analyze',
                description: 'Automated taxonomic classification via Nextflow and the epi2me-labs wf-metagenomics pipeline (Kraken2 + Bracken).',
                iconBg: 'bg-blue-200 dark:bg-blue-800/30',
                iconColor: 'text-blue-700 dark:text-blue-300',
                accentColor: 'from-blue-500 to-blue-600',
              },
              {
                icon: BarChart3,
                number: '03',
                title: 'Visualize',
                description: 'Automatically generated R plots: abundance graphs, heatmaps, and beta diversity analyses.',
                iconBg: 'bg-orange-200 dark:bg-orange-800/30',
                iconColor: 'text-orange-700 dark:text-orange-300',
                accentColor: 'from-orange-500 to-orange-600',
              },
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.2,
                  type: "spring",
                  stiffness: 100
                }}
                viewport={{ once: true }}
                className="group relative"
              >
                {/* Gradient border on hover */}
                <div className={`absolute -inset-[1px] bg-gradient-to-br ${step.accentColor} rounded-3xl opacity-0 group-hover:opacity-100 blur-sm transition-all duration-500`}></div>

                {/* Card */}
                <div className="relative bg-card border border-border rounded-3xl p-8 h-full transition-all duration-500 group-hover:border-transparent group-hover:shadow-2xl group-hover:scale-[1.02]">

                  {/* Step indicator badge */}
                  <div className="flex items-center justify-between mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br ${step.accentColor} text-white font-bold text-lg shadow-lg`}>
                      {step.number}
                    </div>
                  </div>

                  {/* Icon with floating animation */}
                  <motion.div
                    animate={{
                      y: [0, -10, 0],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: index * 0.2
                    }}
                    className={`w-16 h-16 rounded-2xl ${step.iconBg} flex items-center justify-center mb-6 shadow-lg group-hover:shadow-xl transition-shadow duration-300`}
                  >
                    <step.icon className={`h-8 w-8 ${step.iconColor}`} />
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-2xl font-semibold mb-3 group-hover:text-primary transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>

                  {/* Decorative corner accent */}
                  <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${step.accentColor} opacity-0 group-hover:opacity-10 rounded-3xl blur-2xl transition-opacity duration-500`}></div>
                </div>

                {/* Arrow connecting to next card — hidden on the last card (index 2). */}
                {index < 2 && (
                  <div className="hidden md:flex absolute -right-12 top-1/2 -translate-y-1/2 z-30 items-center justify-center w-12 h-12">
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.2 + 0.8, type: "spring" }}
                      viewport={{ once: true }}
                    >
                      <ArrowRight className="h-8 w-8 text-primary" />
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

        </div>
      </section>

      {/* Divider */}
      <div className="container mx-auto px-4">
        <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent"></div>
      </div>

      {/* CTA Section */}
      <section className="relative container mx-auto px-4 py-24 md:py-32 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute -top-20 right-1/4 w-[500px] h-[500px] bg-gradient-to-br from-purple-300/20 to-pink-300/20 dark:from-purple-500/5 dark:to-pink-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 -left-20 w-[600px] h-[600px] bg-gradient-to-br from-blue-300/20 to-purple-300/20 dark:from-blue-500/5 dark:to-purple-500/5 rounded-full blur-3xl"></div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="relative text-center"
        >
          <div className="max-w-3xl mx-auto space-y-8">
            <h2 className="text-5xl md:text-6xl font-bold">
              Get Started
            </h2>

            <p className="text-xl text-muted-foreground font-light max-w-2xl mx-auto">
              Sign up to request access, or log in if you already have an account.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Button size="lg" asChild className="px-8 h-14 text-base shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all">
                <Link to="/signup" className="flex items-center gap-2">
                  Sign Up
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="px-8 h-14 text-base">
                <Link to="/login">Login</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
}
